import { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useApp } from '../context';
import { authApi } from '../lib/api';
import { showToast } from '../lib/toast';
import ScrollDatePicker from './ScrollDatePicker';

const FIELD_CONFIG: Record<
  string,
  { label: string; subtitle?: string; type: string; placeholder: string }
> = {
  name: { label: 'Name', type: 'text', placeholder: 'Enter your name' },
  email: { label: 'Email', type: 'email', placeholder: 'Enter your email' },
  birthday: {
    label: 'Birthday',
    subtitle: "They say it's your birthday, we're gonna have a good time.",
    type: 'date',
    placeholder: '',
  },
};

function formatBirthday(dateStr: string): string {
  if (!dateStr) return '';
  const [y, m, d] = dateStr.split('-');
  const date = new Date(+y, +m - 1, +d);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function EditField() {
  const navigate = useNavigate();
  const { field } = useParams<{ field: string }>();
  const { state, setUser } = useApp();
  const inputRef = useRef<HTMLInputElement>(null);

  const config = field ? FIELD_CONFIG[field] : null;

  const getInitialValue = () => {
    if (!state.user || !field) return '';
    switch (field) {
      case 'name':
        return state.user.name || '';
      case 'email':
        return state.user.email || '';
      case 'birthday':
        return state.user.birthday || '';
      default:
        return '';
    }
  };

  const [value, setValue] = useState(getInitialValue);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (config?.type !== 'date') {
      inputRef.current?.focus();
    }
  }, [config?.type]);

  if (!config || !field) {
    navigate(-1);
    return null;
  }

  const handleSave = async () => {
    if (!state.user) return;
    setSaving(true);
    try {
      if (!state.isGuestMode) {
        const payload: Record<string, string | null> = {};
        payload[field] = field === 'birthday' ? value || null : value.trim();
        const { user: updated } = await authApi.updateProfile(payload);
        setUser({
          ...state.user,
          name: updated.name,
          email: updated.email,
          birthday: updated.birthday,
        });
      } else {
        const updates = { ...state.user };
        if (field === 'name') updates.name = value;
        if (field === 'email') updates.email = value;
        setUser(updates);
      }
      navigate(-1);
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : 'Failed to save', 'error');
    } finally {
      setSaving(false);
    }
  };

  const displayValue = config.type === 'date' && value ? formatBirthday(value) : value;

  return (
    <div className="flex h-dvh w-full flex-col bg-[#f8f5f5] overflow-hidden">
      {/* Header */}
      <div className="flex shrink-0 items-center p-4 pb-2 justify-between bg-[#f8f5f5]/80 backdrop-blur-md z-10">
        <button
          onClick={() => navigate(-1)}
          className="size-10 rounded-full hover:bg-slate-200/50 flex items-center justify-center transition-colors"
        >
          <ArrowLeft size={24} className="text-slate-600" />
        </button>
        <h1 className="text-lg font-bold text-slate-900">{config.label}</h1>
        <div className="size-10" />
      </div>

      <div className="flex-1 overflow-y-auto px-5 pt-6 pb-8">
        {config.subtitle && <p className="text-sm text-slate-500 mb-6">{config.subtitle}</p>}

        {config.type === 'date' ? (
          <>
            <div className="w-full rounded-xl border border-slate-200 bg-white p-4 text-slate-900 font-medium mb-8">
              {displayValue || 'Not set'}
            </div>
            <ScrollDatePicker value={value} onChange={setValue} />
          </>
        ) : (
          <input
            ref={inputRef}
            type={config.type}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={config.placeholder}
            className="w-full rounded-xl border border-slate-200 bg-white p-4 text-slate-900 font-medium focus:ring-2 focus:ring-[#fa8585] focus:border-[#fa8585] outline-none transition-all"
          />
        )}
      </div>

      {/* Save Button */}
      <div className="shrink-0 p-6 pt-3 bg-[#f8f5f5] border-t border-slate-200/50 pb-[max(1.5rem,env(safe-area-inset-bottom))]">
        <button
          onClick={handleSave}
          disabled={saving}
          className="w-full bg-[#fa8585] hover:bg-[#fa8585]/90 text-white font-bold py-4 rounded-xl shadow-lg shadow-[#fa8585]/30 flex items-center justify-center gap-2 active:scale-[0.98] transition-all disabled:opacity-50"
        >
          <span>{saving ? 'Saving...' : 'Save'}</span>
        </button>
      </div>
    </div>
  );
}
